import { useEffect, useState } from "react";
import MaterialTable, { MTableToolbar } from "material-table";
import {
    Box,
    Grid,
    CircularProgress,
    FormControl,
    Typography,
    Paper,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CommonDatePicker from "../../../Base/views/CommonDatePicker";
import SelectableMultiSelect from "../../../Base/views/SelectableMultiSelect";
import SelectableSearch from "../../../Base/views/SelectableSearch";
import { GetSummarizedTimeDataHelper } from "../../../MyTime/helper/Team/TeamHelper";
import { PayPeriodDateHelper } from "../../helper/Reports/GetWageReportHelper";
import { GetEmployeesAction } from "../../../../../store/Employee/Employee/Action";
import { GetUserWorkLocationHelper } from "../../../Employee/helper/EmployeeHelper";
import { useLocation } from "react-router-dom";
import { ConvertDate, formatTimeDuration, getEmployeeId } from "../../../Base/helper/baseFunctions";

const AttendanceSummaryReport = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const date = new Date();
    
    const [isLoading, setIsLoading] = useState(false);
    const [isCsvLoading, setIsCsvLoading] = useState(false);
    const [data, setData] = useState([]);
    const [payPeriod, setPayPeriod] = useState({ start_date: "", end_date: "" });
    const { minDojYear } = useSelector(state => state?.AncillaryReducer?.WorkSpaceDojReducer);
    const { employees_options } = useSelector(state => state?.EmployeeReducer?.EmployeesReducer);

    const getInitialDate = () => {
        if (location?.state?.year && location?.state?.month) {
            const formattedMonth = ("0" + location.state.month).slice(-2);
            return `${location.state.year}-${formattedMonth}-01`;
        }
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    };

    const [filters, setFilters] = useState({
        month_year: getInitialDate(),
        ats_work_location_id: [],
        employee_id: []
    });

    const [worklocationData, setWorklocationData] = useState({
        load: true,
        values: [],
    });

    const [employeesData, setEmployeesData] = useState({
        load: true,
        values: [],
    });

    const handleWorkLoacation = () => {
        GetUserWorkLocationHelper().then(res => {
            if (res?.data?.statuscode === 200) {
                setWorklocationData({ load: false, values: res?.data?.data || [] })
            } else {
                setWorklocationData({ load: false, values: [] })
            }
        })
    }

    useEffect(() => {
        dispatch(GetEmployeesAction({ is_active: true }));
        handleWorkLoacation();
    }, [dispatch]);

    const handleEmployees = () => {
        if (employees_options) {
            setEmployeesData({ load: false, values: employees_options });
        }
    };

    const showWorkLocation = !worklocationData.load && worklocationData.values.length > 1;

    useEffect(() => {
        const [f_year, month] = filters.month_year.split("-");
        const f_month = parseInt(month, 10).toString();

        setIsLoading(true);
        // Step 1: Get Pay Period Dates
        PayPeriodDateHelper({
            month: f_month,
            year: f_year
        }).then((res) => {
            if (res?.data?.statuscode === 200 && res?.data?.data?.length > 0) {
                const period = res.data.data[0];
                setPayPeriod(period);

                // Step 2: Fetch Summarized Attendance Data using the pay period dates
                const locationPayload = Array.isArray(filters?.ats_work_location_id) && filters.ats_work_location_id.length > 0
                    ? JSON.stringify(filters.ats_work_location_id.map((item) => ({ ats_work_location_id: item?.work_location_id })))
                    : null;

                const employeeIdsPayload = Array.isArray(filters?.employee_id) && filters.employee_id.length > 0
                    ? `{${filters.employee_id.map(e => e.employee_id).join(',')}}`
                    : null;

                GetSummarizedTimeDataHelper({
                    from_date: period.start_date,
                    to_date: period.end_date,
                    emp_id: employeeIdsPayload,
                    ats_work_location_json: locationPayload,
                }).then((attendanceRes) => {
                    if (attendanceRes?.data?.statuscode === 200) {
                        setData(attendanceRes?.data?.data || []);
                    } else {
                        setData([]);
                        toast.error(attendanceRes?.message || "Failed to fetch attendance summary data");
                    }
                }).finally(() => {
                    setIsLoading(false);
                });
            } else {
                setPayPeriod({ start_date: "", end_date: "" });
                setData([]);
                setIsLoading(false);
                toast.info("No pay period configured for the selected month/year");
            }
        }).catch((err) => {
            console.error("Error fetching pay period date:", err);
            setIsLoading(false);
        });
    }, [filters]);

    const handleFilter = (event, value) => {
        if (event === "employee_id") {
            setFilters({
                ...filters,
                employee_id: value || []
            });
            return;
        }

        if (event?.target?.name === "month_year") {
            setData([]);
            setFilters({
                ...filters,
                month_year: event.target.value
            });
        }
    };

    const handleFilterChange = (event, value) => {
        if (event === "ats_work_location_id") {
            setFilters({
                ...filters,
                ats_work_location_id: value || []
            });
        }
    };

    const handleExport = async () => {
        setIsCsvLoading(true);
        try {
            const headers = [
                { label: "Name", key: "employee_name" },
                { label: "Days in Month", key: "days_in_month" },
                { label: "Weekoff Days", key: "weekoff_days" },
                { label: "Festival Holidays", key: "festival_holidays" },
                { label: "Optional Holidays", key: "optional_holidays" },
                { label: "Working Days in Month", key: "working_days_in_month" },
                { label: "Actual Worked Days", key: "actual_worked_days" },
                { label: "Actual Worked Hours", key: "actual_worked_time" },
            ];

            const titleStr = payPeriod.start_date && payPeriod.end_date
                ? `Attendance Summary for PayPeriod: ${ConvertDate(payPeriod.start_date)} to ${ConvertDate(payPeriod.end_date)}`
                : 'Attendance Summary';

            const csvData = [[`"${titleStr.replace(/"/g, '""')}"`], [], headers.map(h => h.label)];

            data?.forEach(row => {
                const rowData = headers.map(h => {
                    let value = row[h.key];

                    if (h.key === "actual_worked_time" && value) {
                        value = formatTimeDuration(value);
                    } else if (h.key === "actual_worked_time" && !value) {
                        value = "00:00";
                    }

                    if (value === null || value === undefined) return "";
                    if (typeof value === "string") {
                        return `"${value.replace(/"/g, '""')}"`;
                    }
                    return value;
                });
                csvData.push(rowData);
            });

            const csv = csvData.map(row => row.join(",")).join("\n");
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);

            const downloadLink = document.createElement("a");
            downloadLink.href = url;
            downloadLink.download = "AttendanceSummaryData.csv";
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(`Error generating CSV: ${err.message}`);
        } finally {
            setIsCsvLoading(false);
        }
    };

    return (
        <Box sx={{ maxHeight: "calc(100vh - 125px)", overflowY: "auto", p: 1 }}>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                        <SelectableSearch
                            multiple
                            limitTags={1}
                            label="Employee"
                            value={filters?.employee_id}
                            loading={() => handleEmployees()}
                            fieldLabel="name"
                            variant="outlined"
                            name="employee_id"
                            size="small"
                            onChangeEvent={handleFilter}
                            data={employeesData}
                            isOptionEqualToValue={(option, value) => option?.employee_id === value?.employee_id}
                        />
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                        <CommonDatePicker
                            name="month_year"
                            openTo="month"
                            views={['year', 'month']}
                            label="Month and Year"
                            pickerType="date"
                            size="small"
                            onKeyDown={(e) => e.preventDefault()}
                            onKeyPress={(e) => e.preventDefault()}
                            pickerFormat="MM/yyyy"
                            DefaultDTvalue={filters.month_year}
                            maxDate={new Date()}
                            minDate={new Date(new Date(minDojYear || date).setFullYear(new Date(minDojYear || date).getFullYear() - 1))}
                            handleChange={(value) =>
                                handleFilter({
                                    target: {
                                        name: "month_year",
                                        value: value
                                            ? `${value?.getFullYear()}-${("0" + (value?.getMonth() + 1)).slice(-2)}-${("0" + value?.getDate()).slice(-2)}`
                                            : "",
                                    },
                                })
                            }
                        />
                    </FormControl>
                </Grid>

                {showWorkLocation && (
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth>
                            <SelectableMultiSelect
                                label="Location"
                                size="small"
                                name="ats_work_location_id"
                                value={filters.ats_work_location_id}
                                fieldLabel="work_location_name"
                                data={worklocationData}
                                onChangeEvent={handleFilterChange}
                            />
                        </FormControl>
                    </Grid>
                )}

            </Grid>
 
             <Paper sx={{ mt: 1, width: "100%" }}>
                 <MaterialTable
                     columns={[
                         { title: "Name", field: "employee_name", cellStyle: { padding: "10px" }, headerStyle: { padding: "10px" } },
                         { title: "Days in Month", field: "days_in_month", type: "numeric", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" } },
                         { title: "Weekoff Days", field: "weekoff_days", type: "numeric", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" } },
                         { title: "Festival Holidays", field: "festival_holidays", type: "numeric", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" } },
                         { title: "Optional Holidays", field: "optional_holidays", type: "numeric", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" } },
                         { title: "Working Days in Month", field: "working_days_in_month", type: "numeric", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" } },
                         { title: "Actual Worked Days", field: "actual_worked_days", type: "numeric", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" } },
                         { title: "Actual Worked Hours", field: "actual_worked_time", cellStyle: { padding: "10px", textAlign: "center" }, headerStyle: { padding: "10px", textAlign: "center" }, render: rowData => rowData?.actual_worked_time ? formatTimeDuration(rowData.actual_worked_time) : "00:00" }
                     ]}
                     data={data}
                     isLoading={isLoading}
                     title={payPeriod.start_date && payPeriod.end_date
                         ? `Attendance Summary for PayPeriod: ${ConvertDate(payPeriod.start_date)} to ${ConvertDate(payPeriod.end_date)}`
                         : "Attendance Summary"
                     }
                    options={{
                        rowStyle: { fontSize: "13px" },
                        headerStyle: { padding: "10px 10px 10px 13px", fontWeight: "bold" },
                        sorting: true,
                        search: true,
                        paging: true,
                        pageSize: 25,
                        pageSizeOptions: [25, 50],
                        exportButton: false,
                    }}
                    actions={[
                        {
                            icon: () => isCsvLoading ? <CircularProgress size={24} /> : <CloudDownloadIcon sx={{ color: "rgba(0, 0, 0, 0.54)" }} />,
                            tooltip: "Export CSV",
                            isFreeAction: true,
                            onClick: () => handleExport(),
                            hidden: data?.length === 0
                        }
                    ]}
                    components={{
                        Toolbar: (props) => (
                            <Box sx={{ minHeight: "5px" }}>
                                <MTableToolbar {...props} />
                            </Box>
                        ),
                    }}
                />
            </Paper>
        </Box>
    );
};

export default AttendanceSummaryReport;

